/**
 * A09 — Equipe (F1.8, painel do Admin).
 *
 * O QUE ESTA TELA RESOLVE
 * O Admin precisa responder três perguntas sem abrir o banco: quem trabalha aqui e com qual
 * função, quais aparelhos estão na mão de cada um, e o que fazer quando um aparelho some ou
 * alguém é desligado.
 *
 * A REGRA QUE MANDA NESTE ARQUIVO (docs/technical/03 §6.6 e a migration 2026081903)
 *   REVOGAR NUNCA PODE SIGNIFICAR PERDER EVIDÊNCIA EM SILÊNCIO.
 * Por isso a escolha entre "recolher em 72 h" e "bloquear tudo" nunca aparece como duas
 * palavras soltas: a tela mostra ANTES quantas fotos e quantos itens ainda só existem naquele
 * celular, e escreve o que cada opção faz com esse material. Escolher errado aqui apaga prova
 * de serviço que não existe em nenhum outro lugar.
 *
 * AÇÃO PRINCIPAL (docs/technical/03 §3.5, tabela B, linha A09): **Revogar / Bloquear**.
 * São as únicas ações em destaque visual. Trocar função e filial padrão é secundária de
 * propósito — é o que se faz todo dia, mas não é o que a tela existe para deixar claro.
 *
 * O QUE ESTA TELA NÃO FAZ (e por quê)
 *  - Não mostra o token de recolhimento, mesmo recebendo-o na resposta da RPC. Quem entrega o
 *    token ao aparelho é a Edge, no 401. Um token na tela é uma credencial copiável que dá
 *    direito de despejar arquivo na quarentena — não há motivo operacional para exibi-la.
 *  - Não cria usuário nem reseta 2FA (secundárias de A09, fora do recorte desta entrega).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { pode, porQueNaoPode, type UsuarioSessao } from '../auth/sessao.js';
import { ESTADOS, type EstadoTela } from '../estados/tipos.js';
import { Botao } from '../componentes/Botao.js';
import { Campo } from '../componentes/Campo.js';
import { EstadoDaTela } from '../componentes/EstadoDaTela.js';
import { Tabela, type ColunaTabela } from '../componentes/Tabela.js';

// =====================================================================================
// 1. Contrato de estados desta tela (docs/technical/03 §3.5, tabela B, linha A09)
// =====================================================================================
// A linha da tabela B para A09 é: 1 ● · 2 ● · 3 ◐ · 4 — · 5 ● · 6 ● · 7 ◐ · 8 ● · 9 — · 10 ◐ · 11 ●
// Declarar isto como dado (e não como comentário) permite ao teste conferir que a tela não
// esqueceu um estado nem inventou um que a tabela nega.
export type AplicacaoDeEstado = 'padrao' | 'especifico' | 'nao_se_aplica';

export const ESTADOS_DA_TELA: Readonly<
  Record<EstadoTela, { aplica: AplicacaoDeEstado; nota: string }>
> = {
  carregando: { aplica: 'padrao', nota: 'Esqueleto da lista enquanto a equipe chega.' },
  vazio: {
    aplica: 'especifico',
    // Divergência da tabela B (lá está ●) com justificativa, como o §3.5 exige: a ação padrão
    // do estado "vazio" é "Criar", e criar usuário não acontece nesta tela (o convite sai do
    // Auth). Oferecer "Criar" seria um botão que não leva a lugar nenhum.
    nota: 'Só existe você por aqui; criar usuário não é feito nesta tela, então não há atalho "Criar".',
  },
  sem_internet: {
    aplica: 'especifico',
    // O texto padrão promete "tudo é salvo neste aparelho". Aqui isso seria mentira: nenhuma
    // ação do painel do Admin tem fila local — revogar e bloquear precisam do servidor na hora.
    nota: 'Painel do Admin não tem fila local: sem rede, é leitura antiga e nenhuma ação sai.',
  },
  tiny_indisponivel: {
    aplica: 'nao_se_aplica',
    nota: 'Equipe e aparelhos não dependem do Tiny em nenhum ponto.',
  },
  sem_permissao: { aplica: 'padrao', nota: 'Exige administrar_usuarios e segundo fator (AAL2).' },
  versao_incompativel: {
    aplica: 'padrao',
    nota: 'Painel abaixo da versão mínima fica só de leitura.',
  },
  acao_em_andamento: {
    aplica: 'especifico',
    nota: 'Antes de confirmar, a tela diz quantas fotos e itens ainda só existem no aparelho.',
  },
  falha_recuperavel: { aplica: 'padrao', nota: 'Nada foi alterado; dá para tentar de novo.' },
  conflito: {
    aplica: 'nao_se_aplica',
    nota: 'Não há preenchimento simultâneo de campo aqui; cada ação é decidida pelo servidor na hora.',
  },
  sucesso_parcial: {
    aplica: 'especifico',
    nota: 'Recolhimento em curso: o aparelho ainda tem prazo para devolver.',
  },
  sucesso_completo: { aplica: 'padrao', nota: 'Servidor confirmou a mudança.' },
};

/** Capacidade que o servidor exige nas três RPCs desta tela (migration 2026081903 §7.3–7.5). */
export const CAPACIDADE_DA_TELA = 'administrar_usuarios';

/** Mesmo piso do banco (`length(btrim(p_motivo)) >= 5`): recusar aqui evita uma ida ao servidor. */
export const MOTIVO_MINIMO = 5;

const TEXTO_SEM_INTERNET =
  'Sem internet — esta tela é a última leitura do servidor. Trocar função, bloquear ou revogar só funciona com rede.';
const TEXTO_VAZIO =
  'Ninguém mais cadastrado além de você — peça a quem cuida do sistema para incluir a equipe.';
const TEXTO_FALHA = 'Não deu certo agora — nada foi alterado. Você pode tentar de novo.';

// =====================================================================================
// 2. Tipos do que a tela lê e escreve
// =====================================================================================
export type ModoRevogacao = 'recolher_72h' | 'bloquear_tudo';
export type ModoBloqueio = 'bloquear_com_recolhimento' | 'bloquear_tudo';

export interface PessoaDaEquipe {
  id: string;
  nome: string;
  email: string | null;
  funcao_id: string | null;
  filial_padrao_id: string | null;
  bloqueado_em: string | null;
  ativo: boolean;
}

export interface FuncaoDaEmpresa {
  id: string;
  nome: string;
}

export interface FilialDaEmpresa {
  id: string;
  nome: string;
  ativa: boolean;
}

export interface AparelhoDaPessoa {
  id: string;
  nome_exibicao: string;
  plataforma: 'ios' | 'android' | 'desktop' | 'outro';
  versao_app: string | null;
  estado: 'ativo' | 'revogado' | 'desativado';
  ultimo_contato_em: string | null;
  /** Heartbeat: itens que o aparelho diz ter e que nunca chegaram ao servidor. */
  pendentes_reportados: number;
  pendentes_bytes: number;
  modo_revogacao: ModoRevogacao | null;
  token_recolhimento_expira_em: string | null;
  recolhimento_concluido_em: string | null;
  /** Tablet compartilhado: fila de OUTRA pessoa presa neste aparelho (§3.3 regra 4). */
  fila_de_outras_pessoas: number;
}

export interface EquipeCarregada {
  pessoas: PessoaDaEquipe[];
  funcoes: FuncaoDaEmpresa[];
  filiais: FilialDaEmpresa[];
}

/**
 * Porta de dados da tela. Existe para que o teste prove o COMPORTAMENTO (qual RPC, com qual
 * modo, com qual motivo) sem precisar imitar o construtor de consultas do Supabase — imitação
 * que quebra a cada atualização da biblioteca e não prova nada sobre a regra de produto.
 */
export interface PortaEquipe {
  lerEquipe(): Promise<EquipeCarregada>;
  lerAparelhos(usuarioId: string): Promise<AparelhoDaPessoa[]>;
  salvarCadastro(entrada: {
    usuarioId: string;
    funcaoId: string | null;
    filialPadraoId: string | null;
  }): Promise<void>;
  revogarAparelho(entrada: {
    dispositivoId: string;
    modo: ModoRevogacao;
    motivo: string | null;
  }): Promise<{ token_expira_em: string | null }>;
  bloquearPessoa(entrada: { usuarioId: string; modo: ModoBloqueio; motivo: string }): Promise<void>;
  desbloquearPessoa(entrada: { usuarioId: string; motivo: string }): Promise<void>;
}

// =====================================================================================
// 3. Implementação da porta em cima do Supabase
// =====================================================================================
/**
 * Nome da RPC que grava função e filial padrão.
 *
 * ATENÇÃO — PENDÊNCIA CONHECIDA: até esta entrega, as migrations criam RPC para revogar,
 * bloquear e desbloquear, mas NÃO para editar o cadastro da pessoa; e `public.usuario` é só
 * leitura para o cliente (sem GRANT e sem policy de UPDATE — migration 2026081902 §6). A tela
 * já chama a RPC com a assinatura esperada
 *   public.atualizar_usuario(p_usuario_id uuid, p_funcao_id uuid, p_filial_padrao_id uuid)
 * e, enquanto ela não existir, o PostgREST responde PGRST202 e a pessoa lê uma frase honesta
 * ("ainda não dá para salvar por aqui; nada foi alterado") em vez de um erro técnico.
 */
export const RPC_ATUALIZAR_CADASTRO = 'atualizar_usuario';

const COLUNAS_PESSOA = 'id, nome, email, funcao_id, filial_padrao_id, bloqueado_em, ativo';
const COLUNAS_APARELHO =
  'id, nome_exibicao, plataforma, versao_app, estado, ultimo_contato_em, pendentes_reportados, pendentes_bytes, modo_revogacao, token_recolhimento_expira_em, recolhimento_concluido_em';

export function portaSupabase(): PortaEquipe {
  return {
    async lerEquipe() {
      const cliente = supabase();
      // `usuario_detalhe` e não `usuario`: o GRANT de coluna esconde bloqueio e e-mail na
      // tabela; a visão é quem libera o cadastro completo a quem administra usuários.
      const [pessoas, funcoes, filiais] = await Promise.all([
        cliente.from('usuario_detalhe').select(COLUNAS_PESSOA).order('nome'),
        cliente.from('funcao').select('id, nome').order('nome'),
        cliente.from('filial').select('id, nome, ativa').order('nome'),
      ]);
      if (pessoas.error) throw pessoas.error;
      if (funcoes.error) throw funcoes.error;
      if (filiais.error) throw filiais.error;
      return {
        pessoas: (pessoas.data ?? []) as PessoaDaEquipe[],
        funcoes: (funcoes.data ?? []) as FuncaoDaEmpresa[],
        filiais: (filiais.data ?? []) as FilialDaEmpresa[],
      };
    },

    async lerAparelhos(usuarioId) {
      const cliente = supabase();
      // `dispositivo.usuario_id` é o DONO do aparelho e nunca muda — é por ele que se responde
      // "de quem é este celular que sumiu?".
      const { data, error } = await cliente
        .from('dispositivo')
        .select(COLUNAS_APARELHO)
        .eq('usuario_id', usuarioId)
        .order('ultimo_contato_em', { ascending: false, nullsFirst: false });
      if (error) throw error;

      const aparelhos = (data ?? []) as Omit<AparelhoDaPessoa, 'fila_de_outras_pessoas'>[];
      if (aparelhos.length === 0) return [];

      // Segunda consulta só para o caso do tablet compartilhado: o Admin precisa saber que
      // revogar este aparelho também tranca a fila de outra pessoa que está presa nele.
      const { data: pares, error: erroPares } = await cliente
        .from('dispositivo_usuario')
        .select('dispositivo_id, usuario_id, fila_pendente')
        .in(
          'dispositivo_id',
          aparelhos.map((a) => a.id),
        );
      if (erroPares) throw erroPares;

      const filaDeOutros = new Map<string, number>();
      for (const par of (pares ?? []) as {
        dispositivo_id: string;
        usuario_id: string;
        fila_pendente: number;
      }[]) {
        if (par.usuario_id === usuarioId) continue;
        filaDeOutros.set(
          par.dispositivo_id,
          (filaDeOutros.get(par.dispositivo_id) ?? 0) + par.fila_pendente,
        );
      }

      return aparelhos.map((a) => ({ ...a, fila_de_outras_pessoas: filaDeOutros.get(a.id) ?? 0 }));
    },

    async salvarCadastro({ usuarioId, funcaoId, filialPadraoId }) {
      const { error } = await supabase().rpc(RPC_ATUALIZAR_CADASTRO, {
        p_usuario_id: usuarioId,
        p_funcao_id: funcaoId,
        p_filial_padrao_id: filialPadraoId,
      });
      if (error) throw error;
    },

    async revogarAparelho({ dispositivoId, modo, motivo }) {
      const { data, error } = await supabase().rpc('revogar_dispositivo', {
        p_dispositivo_id: dispositivoId,
        p_modo: modo,
        p_motivo: motivo,
      });
      if (error) throw error;
      // A resposta traz `token_recolhimento`. Ele é lido e DESCARTADO de propósito: quem
      // entrega o token ao aparelho é a Edge, no 401. Ver o cabeçalho deste arquivo.
      const resposta = (data ?? {}) as { token_expira_em?: string | null };
      return { token_expira_em: resposta.token_expira_em ?? null };
    },

    async bloquearPessoa({ usuarioId, modo, motivo }) {
      const { error } = await supabase().rpc('bloquear_usuario', {
        p_usuario_id: usuarioId,
        p_modo: modo,
        p_motivo: motivo,
      });
      if (error) throw error;
    },

    async desbloquearPessoa({ usuarioId, motivo }) {
      const { error } = await supabase().rpc('desbloquear_usuario', {
        p_usuario_id: usuarioId,
        p_motivo: motivo,
      });
      if (error) throw error;
    },
  };
}

// =====================================================================================
// 4. Texto para gente — nada de código HTTP na tela
// =====================================================================================
export function mensagemDeErro(erro: unknown): string {
  const bruto = (erro ?? {}) as { code?: unknown; message?: unknown };
  const codigo = typeof bruto.code === 'string' ? bruto.code : '';
  const texto = typeof bruto.message === 'string' ? bruto.message : '';

  // P0001 é o código que TODAS as nossas RPCs usam para falar com a pessoa: a frase já sai do
  // banco em português operacional (migration 2026081903), então ela é repassada inteira.
  if (codigo === 'P0001') return texto.length > 0 ? texto : TEXTO_FALHA;
  if (codigo === 'PGRST202') {
    return 'Ainda não dá para salvar essa mudança por aqui — nada foi alterado. Avise quem cuida do sistema.';
  }
  if (codigo === '42501' || codigo === 'PGRST301') return ESTADOS.sem_permissao.texto;
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(texto)) {
    return 'Sem internet — não deu para falar com o servidor. Nada foi alterado.';
  }
  return TEXTO_FALHA;
}

export function formatarBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return 'nada';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return 'menos de 1 MB';
  if (mb < 1024) return `${Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1).replace('.', ',')} GB`;
}

export function formatarDataHora(iso: string | null): string {
  if (!iso) return 'sem data';
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return 'sem data';
  return quando.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function descreverUltimoContato(iso: string | null, agora: Date = new Date()): string {
  if (!iso) return 'nunca falou com o servidor';
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return 'nunca falou com o servidor';
  const minutos = Math.floor((agora.getTime() - quando.getTime()) / 60000);
  if (minutos < 1) return 'agora mesmo';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  return `em ${quando.toLocaleDateString('pt-BR')}`;
}

export function descreverSituacaoDaPessoa(pessoa: PessoaDaEquipe): string {
  if (pessoa.bloqueado_em) return `Bloqueado desde ${formatarDataHora(pessoa.bloqueado_em)}`;
  if (!pessoa.ativo) return 'Fora do quadro';
  return 'Trabalhando normalmente';
}

export function descreverSituacaoDoAparelho(
  aparelho: AparelhoDaPessoa,
  agora: Date = new Date(),
): string {
  if (aparelho.estado === 'ativo') return 'Em uso';
  if (aparelho.estado === 'desativado')
    return 'Fora de uso — já devolveu o que tinha e foi encerrado';
  if (aparelho.modo_revogacao === 'bloquear_tudo') {
    return 'Bloqueado por completo — o que estava guardado só nele foi perdido';
  }
  if (aparelho.recolhimento_concluido_em) return 'Recolhimento concluído — devolveu o que tinha';
  const prazo = aparelho.token_recolhimento_expira_em
    ? new Date(aparelho.token_recolhimento_expira_em)
    : null;
  if (prazo && !Number.isNaN(prazo.getTime()) && prazo.getTime() <= agora.getTime()) {
    return 'Prazo de recolhimento vencido — o que estava só nele não volta mais';
  }
  return `Recolhendo o que ficou guardado — até ${formatarDataHora(aparelho.token_recolhimento_expira_em)}`;
}

/** Uma frase, porque a escolha errada custa evidência que só existe naquele celular. */
export const FRASE_DIFERENCA_REVOGACAO =
  'A diferença está no que ainda só existe neste aparelho: recolher em 72 horas tira o acesso agora e ainda deixa o aparelho devolver as fotos e os checklists guardados nele; bloquear tudo tira o acesso agora e perde esse material para sempre.';

export const OPCOES_REVOGACAO: ReadonlyArray<{
  valor: ModoRevogacao;
  rotulo: string;
  frase: string;
}> = [
  {
    valor: 'recolher_72h',
    rotulo: 'Recolher em 72 horas (recomendado)',
    frase:
      'O aparelho para de funcionar agora, mas por 72 horas ainda consegue devolver o que só existe nele.',
  },
  {
    valor: 'bloquear_tudo',
    rotulo: 'Bloquear tudo',
    frase:
      'O aparelho para de funcionar agora e o que estava guardado só nele é perdido para sempre.',
  },
];

export const OPCOES_BLOQUEIO: ReadonlyArray<{
  valor: ModoBloqueio;
  rotulo: string;
  frase: string;
}> = [
  {
    valor: 'bloquear_com_recolhimento',
    rotulo: 'Bloquear e recolher em 72 horas (recomendado)',
    frase:
      'A pessoa perde o acesso agora; os aparelhos dela ainda devolvem, por 72 horas, o que nunca chegou ao servidor.',
  },
  {
    valor: 'bloquear_tudo',
    rotulo: 'Bloquear tudo',
    frase:
      'A pessoa perde o acesso agora e o que estava guardado só nos aparelhos dela é perdido para sempre.',
  },
];

// =====================================================================================
// 5. A tela
// =====================================================================================
/**
 * Por que quase nada de visual mora neste arquivo: `Botao`, `Campo`, `Tabela` e `EstadoDaTela`
 * (em `componentes/`) já carregam as regras que valem para o painel inteiro — botão indisponível
 * que explica o porquê, rótulo ligado ao campo, piso de legibilidade, texto único dos 11 estados.
 * Redesenhar isso aqui seria a duplicação que o produto proíbe: bastaria uma tela esquecer de
 * copiar uma regra para o funcionário aprender duas linguagens diferentes.
 */

const ESTILO_CARTAO: React.CSSProperties = {
  background: 'var(--cor-superficie)',
  border: '1px solid var(--cor-linha)',
  borderRadius: 'var(--raio)',
  boxShadow: 'var(--sombra-sm)',
  padding: 'var(--esp-5)',
  marginTop: 'var(--esp-5)',
};

const ESTILO_FIELDSET: React.CSSProperties = {
  border: '1px solid var(--cor-linha)',
  borderRadius: 'var(--raio-sm)',
  padding: 'var(--esp-4)',
  margin: 0,
};

/**
 * Por que estes blocos escondem a linha "o que acontece com o seu trabalho": ela é verdadeira nas
 * telas do técnico, que têm fila local. O painel do Admin não tem — prometer "a fila tenta sozinha"
 * aqui seria dizer que a revogação vai acontecer sozinha depois, e ela simplesmente não aconteceu.
 */
const PORQUE_SEM_FILA_LOCAL =
  'O painel do Admin não guarda ação em fila local: ou o servidor confirmou, ou nada foi feito.';

type Dialogo =
  | { tipo: 'nenhum' }
  | { tipo: 'revogar'; aparelho: AparelhoDaPessoa; modo: ModoRevogacao; motivo: string }
  | { tipo: 'bloquear'; modo: ModoBloqueio; motivo: string }
  | { tipo: 'desbloquear'; motivo: string };

export interface EquipeProps {
  sessao: UsuarioSessao | null;
  /** Injetável para teste; em produção fala com o Supabase. */
  porta?: PortaEquipe;
  /** O shell avisa quando o app está abaixo da versão mínima (estado 6 da tabela A). */
  versaoIncompativel?: boolean;
}

export function Equipe({ sessao, porta, versaoIncompativel = false }: EquipeProps) {
  const dados = useMemo(() => porta ?? portaSupabase(), [porta]);

  const [carregando, setCarregando] = useState(true);
  const [equipe, setEquipe] = useState<EquipeCarregada>({ pessoas: [], funcoes: [], filiais: [] });
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [aparelhos, setAparelhos] = useState<AparelhoDaPessoa[]>([]);
  const [carregandoAparelhos, setCarregandoAparelhos] = useState(false);
  const [emAndamento, setEmAndamento] = useState<string | null>(null);
  // Duas caixas de mensagem separadas de propósito: `erroLeitura` fala da lista que não chegou
  // (e some sozinho quando ela chega), `recado` fala do resultado da última AÇÃO do Admin. Com
  // uma variável só, recarregar a lista depois de revogar apagava o aviso do recolhimento em
  // curso — ou seja, a tela escondia justamente o que o Admin precisava ler.
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);
  const [recado, setRecado] = useState<{ estado: EstadoTela; texto: string } | null>(null);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [funcaoEscolhida, setFuncaoEscolhida] = useState('');
  const [filialEscolhida, setFilialEscolhida] = useState('');
  const [dialogo, setDialogo] = useState<Dialogo>({ tipo: 'nenhum' });

  // A capacidade e o segundo fator são exigidos pelas três RPCs. Perguntar aqui não é a
  // autorização (essa é do servidor) — é para o botão dizer o porquê em vez de sumir.
  const impedimento = porQueNaoPode(sessao, CAPACIDADE_DA_TELA, true);
  const temCapacidade = pode(sessao, CAPACIDADE_DA_TELA);

  // Nenhum `setState` antes do primeiro `await`: o esqueleto já nasce ligado (`useState(true)`) e
  // só é desligado quando a resposta chega, sem uma segunda renderização só para dizer o que já
  // estava dito.
  const carregarEquipe = useCallback(async () => {
    try {
      const lida = await dados.lerEquipe();
      setEquipe(lida);
      setErroLeitura(null);
    } catch (erro) {
      setErroLeitura(mensagemDeErro(erro));
    } finally {
      setCarregando(false);
    }
  }, [dados]);

  const carregarAparelhos = useCallback(
    async (usuarioId: string) => {
      setCarregandoAparelhos(true);
      try {
        setAparelhos(await dados.lerAparelhos(usuarioId));
        setErroLeitura(null);
      } catch (erro) {
        setAparelhos([]);
        setErroLeitura(mensagemDeErro(erro));
      } finally {
        setCarregandoAparelhos(false);
      }
    },
    [dados],
  );

  // A lista é buscada na abertura. O `setState` só acontece DEPOIS do `await` (dentro de
  // `carregarEquipe`), e não no corpo do efeito — é o caso legítimo de "efeito que busca dado de
  // fora", sem renderização em cascata.
  useEffect(() => {
    void (async () => {
      await carregarEquipe();
    })();
  }, [carregarEquipe]);

  useEffect(() => {
    const mudou = () => setOnline(navigator.onLine);
    window.addEventListener('online', mudou);
    window.addEventListener('offline', mudou);
    return () => {
      window.removeEventListener('online', mudou);
      window.removeEventListener('offline', mudou);
    };
  }, []);

  const pessoa = useMemo(
    () => equipe.pessoas.find((p) => p.id === selecionadaId) ?? null,
    [equipe.pessoas, selecionadaId],
  );

  const abrirPessoa = useCallback(
    (alvo: PessoaDaEquipe) => {
      setSelecionadaId(alvo.id);
      setFuncaoEscolhida(alvo.funcao_id ?? '');
      setFilialEscolhida(alvo.filial_padrao_id ?? '');
      setDialogo({ tipo: 'nenhum' });
      setRecado(null);
      void carregarAparelhos(alvo.id);
    },
    [carregarAparelhos],
  );

  const nomeDaFuncao = useCallback(
    (id: string | null) => equipe.funcoes.find((f) => f.id === id)?.nome ?? 'sem função',
    [equipe.funcoes],
  );
  const nomeDaFilial = useCallback(
    (id: string | null) => equipe.filiais.find((f) => f.id === id)?.nome ?? 'sem filial padrão',
    [equipe.filiais],
  );

  /** Envolve toda ação de escrita: um só lugar para "em andamento", erro e recarga. */
  const executar = useCallback(
    async (rotulo: string, acao: () => Promise<{ estado: EstadoTela; texto: string }>) => {
      setEmAndamento(rotulo);
      setRecado(null);
      try {
        const fim = await acao();
        setRecado(fim);
        setDialogo({ tipo: 'nenhum' });
        await carregarEquipe();
        if (selecionadaId) await carregarAparelhos(selecionadaId);
      } catch (erro) {
        setRecado({ estado: 'falha_recuperavel', texto: mensagemDeErro(erro) });
      } finally {
        setEmAndamento(null);
      }
    },
    [carregarEquipe, carregarAparelhos, selecionadaId],
  );

  const cadastroMudou =
    pessoa !== null &&
    (funcaoEscolhida !== (pessoa.funcao_id ?? '') ||
      filialEscolhida !== (pessoa.filial_padrao_id ?? ''));

  const salvarCadastro = () => {
    if (!pessoa) return;
    void executar('Salvando', async () => {
      await dados.salvarCadastro({
        usuarioId: pessoa.id,
        funcaoId: funcaoEscolhida === '' ? null : funcaoEscolhida,
        filialPadraoId: filialEscolhida === '' ? null : filialEscolhida,
      });
      return {
        estado: 'sucesso_completo',
        texto: `Função e filial padrão de ${pessoa.nome} atualizadas.`,
      };
    });
  };

  const confirmarRevogacao = () => {
    if (dialogo.tipo !== 'revogar') return;
    const { aparelho, modo, motivo } = dialogo;
    if (modo === 'bloquear_tudo' && motivo.trim().length < MOTIVO_MINIMO) {
      setRecado({
        estado: 'falha_recuperavel',
        texto:
          'Escreva o motivo antes de bloquear tudo: este é o caminho que perde de vez o que ainda só existe neste aparelho.',
      });
      return;
    }
    void executar('Revogando', async () => {
      const resultado = await dados.revogarAparelho({
        dispositivoId: aparelho.id,
        modo,
        motivo: motivo.trim() === '' ? null : motivo.trim(),
      });
      if (modo === 'recolher_72h') {
        return {
          estado: 'sucesso_parcial',
          texto: `${aparelho.nome_exibicao} perdeu o acesso. Recolhimento em curso até ${formatarDataHora(resultado.token_expira_em)} — o que estava guardado nele volta sozinho quando o aparelho falar com o servidor.`,
        };
      }
      return {
        estado: 'sucesso_completo',
        texto: `${aparelho.nome_exibicao} foi bloqueado por completo. O que estava guardado só nele não volta mais; o motivo ficou registrado.`,
      };
    });
  };

  const confirmarBloqueio = () => {
    if (dialogo.tipo !== 'bloquear' || !pessoa) return;
    const { modo, motivo } = dialogo;
    // O motivo é obrigatório aqui mesmo quando o banco aceitaria vazio: bloquear alguém é uma
    // decisão que, meses depois, precisa ter explicação escrita ao lado.
    if (motivo.trim().length < MOTIVO_MINIMO) {
      setRecado({
        estado: 'falha_recuperavel',
        texto: 'Escreva o motivo de bloquear o acesso desta pessoa.',
      });
      return;
    }
    const alvo = pessoa;
    void executar('Bloqueando', async () => {
      await dados.bloquearPessoa({ usuarioId: alvo.id, modo, motivo: motivo.trim() });
      if (modo === 'bloquear_com_recolhimento') {
        return {
          estado: 'sucesso_parcial',
          texto: `${alvo.nome} perdeu o acesso. Os aparelhos dela têm 72 horas para devolver o que nunca chegou ao servidor.`,
        };
      }
      return {
        estado: 'sucesso_completo',
        texto: `${alvo.nome} perdeu o acesso e o que estava guardado só nos aparelhos dela foi descartado, com o motivo registrado.`,
      };
    });
  };

  const confirmarDesbloqueio = () => {
    if (dialogo.tipo !== 'desbloquear' || !pessoa) return;
    const motivo = dialogo.motivo;
    if (motivo.trim().length < MOTIVO_MINIMO) {
      setRecado({
        estado: 'falha_recuperavel',
        texto: 'Escreva o motivo de devolver o acesso a esta pessoa.',
      });
      return;
    }
    const alvo = pessoa;
    void executar('Devolvendo acesso', async () => {
      await dados.desbloquearPessoa({ usuarioId: alvo.id, motivo: motivo.trim() });
      return {
        estado: 'sucesso_completo',
        texto: `${alvo.nome} voltou a ter acesso. Os aparelhos revogados dela continuam revogados — ela entra em um aparelho novo.`,
      };
    });
  };

  // "Vazio" aqui significa "ninguém além de você": sem a capacidade `administrar_usuarios`, a
  // visão `usuario_detalhe` devolve só o próprio cadastro, e uma lista com uma linha só — a sua —
  // não é uma equipe; dizer isso é mais útil do que mostrar uma tabela de um nome.
  const listaVazia = !carregando && equipe.pessoas.every((p) => p.id === sessao?.id);

  const colunas: ColunaTabela<PessoaDaEquipe>[] = [
    {
      chave: 'nome',
      titulo: 'Nome',
      celula: (p) => (
        <>
          <span style={{ fontWeight: 'var(--peso-medio)' }}>{p.nome}</span>
          {p.email ? (
            <span style={{ display: 'block', color: 'var(--cor-texto-suave)' }}>{p.email}</span>
          ) : null}
        </>
      ),
    },
    { chave: 'funcao', titulo: 'Função', celula: (p) => nomeDaFuncao(p.funcao_id) },
    { chave: 'filial', titulo: 'Filial padrão', celula: (p) => nomeDaFilial(p.filial_padrao_id) },
    { chave: 'situacao', titulo: 'Situação', celula: (p) => descreverSituacaoDaPessoa(p) },
    {
      chave: 'acao',
      titulo: 'Aparelhos',
      alinharAoFim: true,
      celula: (p) => (
        // Variante "texto": abrir a ficha não pode competir em peso com Revogar/Bloquear, que é a
        // ação principal desta tela (03 §3.5 regra i).
        <Botao variante="texto" onClick={() => abrirPessoa(p)}>
          {`Ver ${p.nome}`}
        </Botao>
      ),
    },
  ];

  return (
    <main
      aria-labelledby="a09-titulo"
      style={{ maxWidth: 'var(--largura-conteudo-max)', margin: '0 auto', padding: 'var(--esp-6)' }}
    >
      <header>
        <p
          style={{
            margin: 0,
            color: 'var(--cor-texto-suave)',
            fontSize: 'var(--font-size-legenda)',
            fontWeight: 'var(--peso-forte)',
            letterSpacing: 'var(--letra-eyebrow)',
            textTransform: 'uppercase',
          }}
        >
          Painel do Admin
        </p>
        <h1
          id="a09-titulo"
          style={{
            margin: 'var(--esp-2) 0 0',
            fontSize: 'var(--font-size-2xl)',
            letterSpacing: 'var(--letra-titulo)',
            lineHeight: 'var(--altura-linha-compacta)',
          }}
        >
          Equipe
        </h1>
        <p style={{ margin: 'var(--esp-2) 0 0', color: 'var(--cor-texto-suave)' }}>
          Quem trabalha no sistema, com qual função e em qual filial — e quais aparelhos estão na
          mão de cada um.
        </p>
      </header>

      <div style={{ display: 'grid', gap: 'var(--esp-3)', marginTop: 'var(--esp-4)' }}>
        {emAndamento ? (
          <EstadoDaTela
            estado="acao_em_andamento"
            especifico={{
              texto: `${emAndamento}…`,
              porque: 'Dizer o que está sendo feito evita o segundo clique que revoga duas vezes.',
            }}
          />
        ) : null}

        {recado ? (
          <EstadoDaTela
            estado={recado.estado}
            especifico={{ texto: recado.texto, porque: PORQUE_SEM_FILA_LOCAL }}
            mostrarDadosLocais={false}
          />
        ) : null}

        {erroLeitura ? (
          <EstadoDaTela
            estado="falha_recuperavel"
            especifico={{ texto: erroLeitura, porque: PORQUE_SEM_FILA_LOCAL }}
            mostrarDadosLocais={false}
            aoAgir={() => void carregarEquipe()}
          />
        ) : null}

        {!temCapacidade ? <EstadoDaTela estado="sem_permissao" /> : null}

        {!online ? (
          <EstadoDaTela
            estado="sem_internet"
            especifico={{
              texto: TEXTO_SEM_INTERNET,
              acao: null,
              porque:
                'O texto padrão promete que tudo é salvo no aparelho. Aqui isso seria mentira: revogar e bloquear precisam do servidor na hora.',
            }}
            mostrarDadosLocais={false}
          />
        ) : null}

        {versaoIncompativel ? <EstadoDaTela estado="versao_incompativel" /> : null}
      </div>

      <section aria-labelledby="a09-lista" style={ESTILO_CARTAO}>
        <h2
          id="a09-lista"
          style={{
            margin: '0 0 var(--esp-4)',
            fontSize: 'var(--font-size-lg)',
            letterSpacing: 'var(--letra-titulo)',
          }}
        >
          Pessoas
        </h2>

        {carregando ? (
          <EstadoDaTela estado="carregando" />
        ) : listaVazia ? (
          <EstadoDaTela
            estado="vazio"
            especifico={{
              texto: TEXTO_VAZIO,
              // Divergência do padrão com justificativa (03 §3.5): a ação padrão do estado vazio é
              // "Criar", e criar usuário não acontece nesta tela — o convite sai do Auth.
              acao: null,
              porque:
                'Criar usuário não é feito aqui; oferecer "Criar" seria um botão sem destino.',
            }}
          />
        ) : (
          <Tabela
            legenda={`${equipe.pessoas.length} pessoa(s) cadastrada(s)`}
            colunas={colunas}
            linhas={equipe.pessoas}
            chaveDaLinha={(p) => p.id}
          />
        )}
      </section>

      {pessoa ? (
        <section aria-labelledby="a09-pessoa" style={ESTILO_CARTAO}>
          <h2
            id="a09-pessoa"
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              letterSpacing: 'var(--letra-titulo)',
            }}
          >
            {pessoa.nome}
          </h2>
          <p style={{ margin: 'var(--esp-1) 0 0', color: 'var(--cor-texto-suave)' }}>
            {descreverSituacaoDaPessoa(pessoa)}
          </p>

          {/* ---------- Cadastro: função e filial padrão ---------- */}
          <div
            style={{
              display: 'grid',
              gap: 'var(--esp-4)',
              marginTop: 'var(--esp-5)',
              maxWidth: '32rem',
            }}
          >
            <Campo
              id="a09-funcao"
              tipo="lista"
              rotulo="Função"
              ajuda="A função decide o que a pessoa consegue fazer no sistema. Sem função escolhida, ela entra e não consegue fazer nada."
              valor={funcaoEscolhida}
              aoMudar={setFuncaoEscolhida}
              motivoDesabilitado={impedimento}
              opcoes={equipe.funcoes.map((f) => ({ valor: f.id, rotulo: f.nome }))}
            />

            <Campo
              id="a09-filial"
              tipo="lista"
              rotulo="Filial padrão"
              ajuda="É a filial que já vem escolhida quando a pessoa abre um processo novo."
              valor={filialEscolhida}
              aoMudar={setFilialEscolhida}
              motivoDesabilitado={impedimento}
              opcoes={equipe.filiais.map((f) => ({
                valor: f.id,
                rotulo: f.ativa ? f.nome : `${f.nome} (inativa)`,
              }))}
            />

            <div>
              <Botao
                variante="secundario"
                carregando={emAndamento === 'Salvando'}
                // Desabilitar em silêncio é justamente o defeito que esta tela existe para não ter:
                // sem mudança nenhuma, o botão diz isso em vez de só ficar cinza.
                motivoDesabilitado={
                  impedimento ??
                  (cadastroMudou
                    ? null
                    : 'Nada mudou ainda — escolha outra função ou outra filial padrão.')
                }
                onClick={salvarCadastro}
              >
                Salvar função e filial
              </Botao>
            </div>
          </div>

          {/* ---------- Bloqueio / desbloqueio ---------- */}
          <div
            style={{
              marginTop: 'var(--esp-6)',
              paddingTop: 'var(--esp-5)',
              borderTop: '1px solid var(--cor-linha)',
            }}
          >
            <h3 style={{ margin: '0 0 var(--esp-2)', fontSize: 'var(--font-size-md)' }}>
              Acesso ao sistema
            </h3>

            {pessoa.bloqueado_em ? (
              <>
                <p style={{ color: 'var(--cor-texto-suave)' }}>
                  Bloqueada desde {formatarDataHora(pessoa.bloqueado_em)}. Devolver o acesso não
                  devolve os aparelhos revogados: ela volta em um aparelho novo.
                </p>
                {dialogo.tipo === 'desbloquear' ? (
                  <div style={{ display: 'grid', gap: 'var(--esp-3)', maxWidth: '32rem' }}>
                    <Campo
                      id="a09-motivo-desbloqueio"
                      tipo="longo"
                      linhas={3}
                      rotulo="Motivo de devolver o acesso"
                      obrigatorio
                      valor={dialogo.motivo}
                      aoMudar={(v) => setDialogo({ tipo: 'desbloquear', motivo: v })}
                    />
                    <div style={{ display: 'flex', gap: 'var(--esp-3)', flexWrap: 'wrap' }}>
                      <Botao
                        variante="perigo"
                        carregando={emAndamento === 'Devolvendo acesso'}
                        motivoDesabilitado={impedimento}
                        onClick={confirmarDesbloqueio}
                      >
                        Devolver o acesso
                      </Botao>
                      <Botao variante="texto" onClick={() => setDialogo({ tipo: 'nenhum' })}>
                        Cancelar
                      </Botao>
                    </div>
                  </div>
                ) : (
                  <Botao
                    variante="perigo"
                    motivoDesabilitado={impedimento}
                    onClick={() => setDialogo({ tipo: 'desbloquear', motivo: '' })}
                  >
                    Devolver o acesso
                  </Botao>
                )}
              </>
            ) : dialogo.tipo === 'bloquear' ? (
              <div style={{ display: 'grid', gap: 'var(--esp-3)', maxWidth: '38rem' }}>
                <p style={{ margin: 0 }}>{FRASE_DIFERENCA_REVOGACAO}</p>
                <fieldset style={ESTILO_FIELDSET}>
                  <legend style={{ fontWeight: 'var(--peso-medio)' }}>
                    O que fazer com o que está nos aparelhos
                  </legend>
                  {OPCOES_BLOQUEIO.map((opcao) => (
                    <label
                      key={opcao.valor}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: 'var(--esp-3)',
                        marginTop: 'var(--esp-3)',
                      }}
                    >
                      <input
                        type="radio"
                        name="a09-modo-bloqueio"
                        value={opcao.valor}
                        checked={dialogo.modo === opcao.valor}
                        onChange={() =>
                          setDialogo({
                            tipo: 'bloquear',
                            modo: opcao.valor,
                            motivo: dialogo.motivo,
                          })
                        }
                      />
                      <span>
                        <strong style={{ fontWeight: 'var(--peso-medio)' }}>{opcao.rotulo}</strong>
                        <span style={{ display: 'block', color: 'var(--cor-texto-suave)' }}>
                          {opcao.frase}
                        </span>
                      </span>
                    </label>
                  ))}
                </fieldset>
                <Campo
                  id="a09-motivo-bloqueio"
                  tipo="longo"
                  linhas={3}
                  rotulo="Motivo do bloqueio"
                  obrigatorio
                  valor={dialogo.motivo}
                  aoMudar={(v) => setDialogo({ tipo: 'bloquear', modo: dialogo.modo, motivo: v })}
                />
                <div style={{ display: 'flex', gap: 'var(--esp-3)', flexWrap: 'wrap' }}>
                  <Botao
                    variante="perigo"
                    carregando={emAndamento === 'Bloqueando'}
                    motivoDesabilitado={impedimento}
                    onClick={confirmarBloqueio}
                  >
                    Bloquear o acesso
                  </Botao>
                  <Botao variante="texto" onClick={() => setDialogo({ tipo: 'nenhum' })}>
                    Cancelar
                  </Botao>
                </div>
              </div>
            ) : (
              <Botao
                variante="perigo"
                motivoDesabilitado={impedimento}
                onClick={() =>
                  setDialogo({ tipo: 'bloquear', modo: 'bloquear_com_recolhimento', motivo: '' })
                }
              >
                Bloquear o acesso
              </Botao>
            )}
          </div>

          {/* ---------- Aparelhos ---------- */}
          <div
            style={{
              marginTop: 'var(--esp-6)',
              paddingTop: 'var(--esp-5)',
              borderTop: '1px solid var(--cor-linha)',
            }}
          >
            <h3 style={{ margin: '0 0 var(--esp-3)', fontSize: 'var(--font-size-md)' }}>
              Aparelhos
            </h3>

            {carregandoAparelhos ? (
              <EstadoDaTela estado="carregando" />
            ) : aparelhos.length === 0 ? (
              <EstadoDaTela
                estado="vazio"
                especifico={{
                  texto: 'Nenhum aparelho registrado para esta pessoa.',
                  acao: null,
                  porque:
                    'Aparelho se registra sozinho no primeiro login; não há o que criar aqui.',
                }}
              />
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'grid',
                  gap: 'var(--esp-4)',
                }}
              >
                {aparelhos.map((aparelho) => (
                  <li
                    key={aparelho.id}
                    style={{
                      border: '1px solid var(--cor-linha)',
                      borderRadius: 'var(--raio-sm)',
                      padding: 'var(--esp-4)',
                      background: 'var(--cor-superficie-2)',
                    }}
                  >
                    <p style={{ margin: 0, fontWeight: 'var(--peso-medio)' }}>
                      {aparelho.nome_exibicao}
                    </p>
                    <p style={{ margin: 'var(--esp-1) 0 0', color: 'var(--cor-texto-suave)' }}>
                      {descreverSituacaoDoAparelho(aparelho)} · último contato{' '}
                      {descreverUltimoContato(aparelho.ultimo_contato_em)}
                    </p>
                    <p style={{ margin: 'var(--esp-2) 0 0' }}>
                      {aparelho.pendentes_reportados > 0
                        ? `${aparelho.pendentes_reportados} item(ns) (${formatarBytes(aparelho.pendentes_bytes)}) ainda só existem neste aparelho.`
                        : 'Nada preso neste aparelho — tudo já chegou ao servidor.'}
                    </p>
                    {aparelho.fila_de_outras_pessoas > 0 ? (
                      <p style={{ margin: 'var(--esp-1) 0 0', color: 'var(--cor-alerta)' }}>
                        {`Fila de outra pessoa presa neste aparelho: ${aparelho.fila_de_outras_pessoas} item(ns).`}
                      </p>
                    ) : null}

                    {dialogo.tipo === 'revogar' && dialogo.aparelho.id === aparelho.id ? (
                      <div
                        style={{ display: 'grid', gap: 'var(--esp-3)', marginTop: 'var(--esp-4)' }}
                      >
                        {/* Estado 7 da tabela B para A09: o aviso com a contagem vem ANTES de confirmar. */}
                        <EstadoDaTela
                          estado="acao_em_andamento"
                          especifico={{
                            texto:
                              aparelho.pendentes_reportados > 0
                                ? `Atenção: ${aparelho.pendentes_reportados} item(ns) (${formatarBytes(aparelho.pendentes_bytes)}) ainda só existem neste aparelho.`
                                : 'Este aparelho não tem nada guardado só nele neste momento.',
                            porque:
                              'A tabela B manda avisar a contagem antes de confirmar: é o número que muda a escolha do Admin.',
                          }}
                        />
                        <p style={{ margin: 0 }}>{FRASE_DIFERENCA_REVOGACAO}</p>
                        <fieldset style={ESTILO_FIELDSET}>
                          <legend style={{ fontWeight: 'var(--peso-medio)' }}>Como revogar</legend>
                          {OPCOES_REVOGACAO.map((opcao) => (
                            <label
                              key={opcao.valor}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                gap: 'var(--esp-3)',
                                marginTop: 'var(--esp-3)',
                              }}
                            >
                              <input
                                type="radio"
                                name="a09-modo-revogacao"
                                value={opcao.valor}
                                checked={dialogo.modo === opcao.valor}
                                onChange={() =>
                                  setDialogo({
                                    tipo: 'revogar',
                                    aparelho,
                                    modo: opcao.valor,
                                    motivo: dialogo.motivo,
                                  })
                                }
                              />
                              <span>
                                <strong style={{ fontWeight: 'var(--peso-medio)' }}>
                                  {opcao.rotulo}
                                </strong>
                                <span style={{ display: 'block', color: 'var(--cor-texto-suave)' }}>
                                  {opcao.frase}
                                </span>
                              </span>
                            </label>
                          ))}
                        </fieldset>
                        <Campo
                          id="a09-motivo-revogacao"
                          tipo="longo"
                          linhas={3}
                          rotulo="Motivo"
                          obrigatorio={dialogo.modo === 'bloquear_tudo'}
                          ajuda={
                            dialogo.modo === 'bloquear_tudo'
                              ? 'Sem motivo escrito o servidor recusa — perda de evidência não pode ficar anônima.'
                              : 'Opcional, mas ajuda quem for conferir isto depois.'
                          }
                          valor={dialogo.motivo}
                          aoMudar={(v) =>
                            setDialogo({ tipo: 'revogar', aparelho, modo: dialogo.modo, motivo: v })
                          }
                        />
                        <div style={{ display: 'flex', gap: 'var(--esp-3)', flexWrap: 'wrap' }}>
                          <Botao
                            variante="perigo"
                            carregando={emAndamento === 'Revogando'}
                            motivoDesabilitado={impedimento}
                            onClick={confirmarRevogacao}
                          >
                            Confirmar revogação
                          </Botao>
                          <Botao variante="texto" onClick={() => setDialogo({ tipo: 'nenhum' })}>
                            Cancelar
                          </Botao>
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: 'var(--esp-3)' }}>
                        <Botao
                          variante="perigo"
                          // O banco recusa qualquer nova revogação depois de `bloquear_tudo` (a chave
                          // local do aparelho já foi apagada). A frase evita que o Admin descubra
                          // isso clicando e levando um erro.
                          motivoDesabilitado={
                            aparelho.modo_revogacao === 'bloquear_tudo'
                              ? 'Este aparelho já foi bloqueado por completo — não há mais nada para recolher nem para revogar.'
                              : impedimento
                          }
                          onClick={() =>
                            setDialogo({
                              tipo: 'revogar',
                              aparelho,
                              modo: 'recolher_72h',
                              motivo: '',
                            })
                          }
                        >
                          {`Revogar ${aparelho.nome_exibicao}`}
                        </Botao>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ) : null}
    </main>
  );
}
